import { FigureSettingsDto } from "aic";
import { ComparatorTypeDto } from "components/annotations/interface";
import { IDropdownOption } from "components/common/HighButton";
import { IHierarchyProperty } from "components/hierarchypage/HierarchyProperties";
import { eApplicationPermission } from "functions/api/enums";
import { HelpLink } from "functions/api/helplinks/loadHelpLinks";
import { HierarchyReadDto } from "functions/api/hierarchy/HierarchyReadDto";
import { HierarchyUpdateDto } from "functions/api/hierarchy/saveHierarchyData";
import { IRequestStatus } from "functions/api/interface";
import { StyleReadDto } from "functions/api/styles/getAllStyles";
import { TlfItemSaveResult } from "functions/api/tlfItem/saveTlfItem";
import { TlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { Patch } from "rfc6902";
import { HierarchyContextProps, IHierarchyAction } from "../hierarchy/interface";
import { ITlfAction, TlfContextProps } from "../tlf/interface";
import type { IMosaicTocExpanded, ITocAction } from "../toc/interface";
import { ISaveBlockers } from "./saveBlockers";

export const UNDO = "UNDO";
export const REDO = "REDO";

/**
 * Reversible changes to editable data across the unified context.
 */
export interface IUnifiedHistoryEntry {
  forward: Patch;
  reverse: Patch;
}

/**
 * Moves through the unified editable-data history.
 */
export interface IUnifiedHistoryAction {
  operation: typeof UNDO | typeof REDO;
  steps?: number;
  calledFrom?: string;
}

/**
 * Application settings
 */
export interface IAppSettings {
  annotationComparatorTypes: ComparatorTypeDto[];
  appSettingsLoaded: boolean;
  applicationPermissions: eApplicationPermission[];
  backEndUrl: string;
  backEndVersion: string;
  colWidthMod: number;
  crystalUrl: string;
  defaultColumnWidth: number;
  defaultDownloadOptions: IDropdownOption[];
  defaultLevelProperties: { [key: string]: IHierarchyProperty[] };
  defaultTableStyle: React.CSSProperties;
  displayName: string;
  domain: string;
  figureSettings: FigureSettingsDto;
  helpLinks: HelpLink[];
  isAdmin: boolean | null;
  maintenanceMessage: string;
  maintenanceMode: boolean;
  prid: string;
  saveBlockers: ISaveBlockers;
  styles: StyleReadDto[];
  userId: number;
  allowedReportTypes: string[];
}

/**
 * Unified application state combining app-level settings with hierarchy and ToC slices.
 * Extends {@link IAppSettings} so that existing consumers can use this type where
 * {@link IAppSettings} is expected.
 */
export interface IUnifiedState extends IAppSettings {
  /** Shared status of the current Save, Save As, restore or template request. */
  saveRequest: IRequestStatus<unknown>;
  /** Chronological reversible patches, never full slice snapshots. */
  undoHistory: IUnifiedHistoryEntry[];
  /** Number of history entries currently applied. */
  undoIndex: number;
  /** Current hierarchy slice managed by the unified reducer */
  hierarchyState: HierarchyContextProps;
  /** Current ToC slice managed by the unified reducer */
  tocState: IMosaicTocExpanded;
  /** Current TLF slice managed by the unified reducer */
  tlfState: TlfContextProps;
}

/**
 * Reconciles the single request selected by the application Save handler.
 */
export const SAVE_COMPLETED = "SAVE_COMPLETED";

/**
 * Applies one successful TLF save across the application state slices.
 */
export const SAVE_TLF_COMPLETED = "SAVE_TLF_COMPLETED";

/**
 * Updates the application-owned validation blocker for a hierarchy property.
 */
export const SET_HIERARCHY_VALIDATION_SAVE_BLOCKER = "SET_HIERARCHY_VALIDATION_SAVE_BLOCKER";

/**
 * Updates the application-owned save request status from loadWrapper.
 */
export const SET_SAVE_REQUEST = "SET_SAVE_REQUEST";

/**
 * Replaces the validation blocker associated with a hierarchy property.
 */
export interface IAppActionSetHierarchyValidationSaveBlocker {
  operation: typeof SET_HIERARCHY_VALIDATION_SAVE_BLOCKER;
  propertyKey: "hierarchyName" | "phase" | "reportType";
  warningText?: string;
}

/**
 * Request lifecycle information shared by all save controls.
 */
export interface IAppActionSetSaveRequest {
  operation: typeof SET_SAVE_REQUEST;
  saveRequest: IRequestStatus<unknown>;
}

/**
 * Save result and snapshots needed to preserve edits made while the request was in flight.
 */
export interface IAppActionSaveCompleted {
  operation: typeof SAVE_COMPLETED;
  result: TlfItemSaveResult | { hierarchy: HierarchyReadDto; tlfItem?: undefined };
  isNewTlf: boolean;
  submittedHierarchy: HierarchyUpdateDto;
  submittedTlfId?: string;
  submittedTlf?: TlfItemReadDto;
  /** Composed variant view submitted alongside the persisted baseline master. */
  submittedVariantView?: TlfItemReadDto;
}

/**
 * Server save result and submission snapshots used for route-safe atomic state reconciliation.
 */
export interface IAppActionSaveTlfCompleted {
  operation: typeof SAVE_TLF_COMPLETED;
  result: TlfItemSaveResult;
  isNewTlf: boolean;
  /** Source editor identity at submission; creation may return a different TLF ID. */
  submittedTlfId: string;
  /** Editable hierarchy snapshot at submission, even when the request did not save hierarchy data. */
  submittedHierarchy: HierarchyUpdateDto;
  /** TLF values actually submitted, used to retain subsequent editor changes. */
  submittedTlf?: TlfItemReadDto;
  /** Composed variant view retained if returned server patches cannot be applied. */
  submittedVariantView?: TlfItemReadDto;
}

/**
 * The combined action type for the unified AppSettings context reducer.
 * Encompasses hierarchy and ToC domain actions dispatched from their respective providers.
 */
export type IUnifiedAction =
  | IAppActionSaveCompleted
  | IAppActionSaveTlfCompleted
  | IAppActionSetHierarchyValidationSaveBlocker
  | IAppActionSetSaveRequest
  | IHierarchyAction
  | ITlfAction
  | ITocAction
  | IUnifiedHistoryAction;

/**
 * The value provided by {@link AppSettingsContext}, following the ARS-style
 * `{ state, dispatch }` pattern.
 */
export interface IAppSettingsContextValue {
  /** Unified application state including app settings, hierarchy, and ToC slices */
  state: IUnifiedState;
  /** Dispatcher for all unified operations across hierarchy and ToC domains */
  dispatch: React.Dispatch<IUnifiedAction>;
}
